import cn from "classnames";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, Fragment, ComponentProps } from "react";

// MUI components
import { Box, Divider, Typography, Paper } from "@mui/material";

// Components
import AccountSettings from "components/AccountManager/AccountSettings";
import PrivacySettings from "components/AccountManager/PrivacySettings";
import DashboardHeader from "components/PaceLabsDashboard/DashboardHeader/DashboardHeader";

// Hooks
import useAuth from "utils/hooks/useAuth";

// Services
import { getUserService, TuserData } from "services";

// Styles
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";
import { RequestInfo } from "store/slice/store.types";
import { useAppSelector } from "app/hooks";
import { getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// Types
import { UserOrgs } from "services/user/user.types";

const useStyles = makeStyles((theme: Theme) => ({
    menu: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(5),
        "& a": {
            padding: theme.spacing(0, 2),
            textDecoration: "none",
        },
    },
    divider: {
        borderColor: theme.palette.neutrals.dark,
        height: "auto",
    },
    optionTitle: {
        color: theme.palette.text.secondary,
    },
    optionTitleActive: {
        color: theme.palette.text.primary,
    },
    main: {
        marginLeft: theme.spacing(8),
        marginRight: theme.spacing(8),
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        marginBottom: theme.spacing(2),
    },
}));

type userData = {
    data: TuserData;
    requestStatus: RequestInfo;
};

export const initialPassword = {
    current: "",
    new: "",
    confirm_new: "",
    status: "pristine" as const,
    errorCurrentPass: undefined,
    errorNewPass: undefined,
};

export type Hub = UserOrgs & {
    sharing: boolean;
};
const AccountManager = () => {
    const classes = useStyles();
    const intl = useIntl();
    const { pathname } = useLocation();

    const userOrganizations = useAppSelector(getUserOrgs);
    const { sessionData, loggedInUserData } = useAuth();

    const [idSelected, setIdSelected] = useState(0);
    const [userData, setUserData] = useState<userData>({ data: {} as TuserData, requestStatus: "pristine" });

    // Account Settings
    const [name, setName] = useState<ComponentProps<typeof AccountSettings>["name"]>({
        firstName: "",
        lastName: "",
        status: "pristine",
    });
    const [email, setEmail] = useState<ComponentProps<typeof AccountSettings>["email"]>({
        value: sessionData?.authn.user.username || "",
        status: "pristine",
        invalid: false,
    });
    const [password, setPassword] = useState<ComponentProps<typeof AccountSettings>["password"]>({
        current: "",
        new: "",
        confirm_new: "",
        status: "pristine",
        errorCurrentPass: undefined,
        errorNewPass: undefined,
    });

    // Privacy Settings
    const [dataPrivacyChecked, setDataPrivacyChecked] = useState(Boolean(userData.data.optin_share_data));
    const [hubAccess, setHubAccess] = useState<Hub[]>([]);

    useEffect(() => {
        if (userOrganizations.status !== "done" || userData.requestStatus !== "done") {
            return;
        }

        const shareDataArray = Array.isArray(userData.data.org_share_data) ? userData.data.org_share_data : [];

        setHubAccess(
            userOrganizations.data.map((hub) => {
                const shareConfig = shareDataArray.find((org) => org.org_id === hub.id);

                return {
                    ...hub,
                    sharing:
                        shareConfig && typeof shareConfig.org_share_data === "boolean"
                            ? shareConfig.org_share_data
                            : false,
                };
            })
        );
    }, [userOrganizations.status, userOrganizations.data, userData.requestStatus, userData.data.org_share_data]);

    const options = useMemo(() => {
        return [
            {
                id: 0,
                title: intl.formatMessage({ id: "sidebar.account.settings" }),
                path: "/account-management/settings",
                component: (
                    <AccountSettings
                        name={name}
                        setName={setName}
                        email={email}
                        setEmail={setEmail}
                        password={password}
                        setPassword={setPassword}
                        userData={userData.data}
                        sessionData={sessionData}
                        isLoading={userData.requestStatus === "fetching"}
                    />
                ),
            },
            {
                id: 1,
                title: intl.formatMessage({ id: "sidebar.account.privacy" }),
                path: "/account-management/privacy",
                component: (
                    <PrivacySettings
                        userData={userData.data}
                        dataPrivacyChecked={dataPrivacyChecked}
                        setDataPrivacyChecked={setDataPrivacyChecked}
                        hubAccess={hubAccess}
                        setHubAccess={setHubAccess}
                        isLoading={userData.requestStatus === "fetching"}
                    />
                ),
            },
        ];
    }, [
        dataPrivacyChecked,
        email,
        hubAccess,
        intl,
        name,
        password,
        sessionData,
        userData.data,
        userData.requestStatus,
    ]);

    useEffect(() => {
        const getUser = async () => {
            setUserData((currentVal) => ({ ...currentVal, requestStatus: "fetching" }));
            try {
                const { data } = await getUserService();
                setUserData({ data, requestStatus: "done" });
                setUserData((currentVal) => ({ ...currentVal, requestStatus: "done" }));
                setDataPrivacyChecked(Boolean(data.optin_share_data));
                setName((currentVal) => ({
                    ...currentVal,
                    firstName: data.auth_data.firstName,
                    lastName: data.auth_data.lastName,
                }));
            } catch (error) {
                console.error(error);
                setUserData((currentVal) => ({ ...currentVal, requestStatus: "error" }));
            }
        };
        getUser();
    }, []);

    useEffect(() => {
        if (!pathname) {
            return;
        }
        const selectedOption = options.find((option) => option.path.includes(pathname));
        if (selectedOption) {
            setIdSelected(selectedOption.id);
        }
    }, [options, pathname]);

    return (
        <Box>
            <DashboardHeader username={loggedInUserData.name} />
            <Box className={classes.menu}>
                {options.map((option, index) => {
                    return (
                        <Fragment key={option.id}>
                            <Link
                                to={option.path}
                                className={cn(classes.optionTitle, {
                                    [classes.optionTitleActive]: index === idSelected,
                                })}
                            >
                                <Typography>{option.title}</Typography>
                            </Link>
                            {index + 1 !== options.length ? (
                                <Divider orientation="vertical" className={classes.divider} />
                            ) : null}
                        </Fragment>
                    );
                })}
            </Box>
            <Box className={classes.main}>
                <Typography variant="h5" className={classes.title}>
                    {options[idSelected].title}
                </Typography>
                <Box>{options[idSelected].component}</Box>
            </Box>
        </Box>
    );
};

export default AccountManager;
